import { Component } from '@wonderlandengine/api';

// ─────────────────────────────────────────────────────────────────────────────
//  crafting-system.js — CraftingSystem WLE Component
//
//  Registers as window.CraftingSystem and drives:
//    - Recipe validation & ingredient deduction
//    - Timed craft progress (update loop)
//    - Shield Cell consumption (shield restore)
//    - Broadcasts crafted/consumed events to multiplayer
//
//  Recipes live here so both VR and desktop share the same data.
//  The index.html GameInterface bridge calls this component's public API.
//
//  Global side-effects:
//    window.CraftingSystem       reference to this component
//
//  GameInterface calls this component makes:
//    .updateShieldCells(count)
//    .updateCraftProgress(recipeId, 0–1)
//    .updateResources(alloy, biomass, titanium, plasma, crystal)
//    .pushConsoleLog(msg)
//    .refreshCraftPanel()         — optional; index.html implements it
// ─────────────────────────────────────────────────────────────────────────────

export const CRAFT_RECIPES = [
    {
        id:         'shieldCell',
        name:       'SHIELD CELL',
        icon:       '🔋',
        desc:       '+50 SHIELD RESTORE  ·  MAX 5 CARRIED',
        inputs:     { plasma: 3, alloy: 2 },
        output:     { type: 'shieldCells', amount: 1 },
        craftTime:  2.5,   // seconds
        maxCarry:   5,
    },
    {
        id:         'medkit',
        name:       'MEDKIT',
        icon:       '💉',
        desc:       '+40 HEALTH RESTORE  ·  MAX 3 CARRIED',
        inputs:     { biomass: 4, alloy: 2 },
        output:     { type: 'medkits', amount: 1 },
        craftTime:  2.0,
        maxCarry:   3,
    },
];

const SHIELD_RESTORE = 50;
const HEALTH_RESTORE = 40;

// Dynamic caps that respect meta-upgrade levels stored by GameLoop._applyMetaUpgrades
function _effectiveMaxShield() { return 100 + (window.DeadVoxMeta?.shieldLevel  || 0) * 8;  }
function _effectiveMaxHealth() { return 100 + (window.DeadVoxMeta?.startHpLevel || 0) * 25; }

export class CraftingSystem extends Component {
    static TypeName = 'crafting-system';

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    start() {
        window.CraftingSystem = this;

        this._craftingId      = null;  // active recipe id
        this._craftTimer      = 0;    // seconds remaining
        this._pendingRefund   = null; // { mat: amount } for cancel

        // Ensure inventory has crafted-item slots
        this._ensureInventorySlots();

        // Bridge to GameInterface (called from index.html buttons / keyboard)
        window.GameInterface = window.GameInterface || {};
        window.GameInterface.craft         = (id) => this.startCraft(id);
        window.GameInterface.cancelCraft   = ()   => this.cancelCraft();
        window.GameInterface.useShieldCell = ()   => this.useShieldCell();
        window.GameInterface.useMedkit     = ()   => this.useMedkit();
        window.GameInterface.canCraft      = (id) => this.canCraft(id);
        window.GameInterface.getRecipes    = ()   => CRAFT_RECIPES;

        console.log('[CraftingSystem] Initialised');
    }

    update(dt) {
        if (!this._craftingId) return;
        this._craftTimer -= dt;

        const recipe = this._getRecipe(this._craftingId);
        if (recipe) {
            const pct = Math.min(1, 1 - this._craftTimer / recipe.craftTime);
            window.GameInterface?.updateCraftProgress?.(this._craftingId, pct);
        }

        if (this._craftTimer <= 0) {
            this._completeCraft();
        }
    }

    // ── Public API ────────────────────────────────────────────────────────────

    canCraft(recipeId) {
        const recipe = this._getRecipe(recipeId);
        if (!recipe) return false;
        const inv = this._getInventory();
        if (!inv) return false;
        if ((inv[recipe.output.type] || 0) >= recipe.maxCarry) return false;
        for (const [mat, need] of Object.entries(recipe.inputs)) {
            if ((inv[mat] || 0) < need) return false;
        }
        return true;
    }

    startCraft(recipeId) {
        if (this._craftingId) {
            window.GameInterface?.pushConsoleLog?.('CRAFTING IN PROGRESS');
            return false;
        }
        const recipe = this._getRecipe(recipeId);
        if (!recipe) return false;

        if (!this.canCraft(recipeId)) {
            const inv = this._getInventory() || {};
            const needs = Object.entries(recipe.inputs)
                .filter(([k, v]) => (inv[k] || 0) < v)
                .map(([k, v]) => `${v - (inv[k] || 0)} ${k.toUpperCase()}`)
                .join(', ');
            window.GameInterface?.pushConsoleLog?.(`INSUFFICIENT: NEED ${needs}`);
            return false;
        }

        const inv = this._getInventory();
        for (const [mat, amount] of Object.entries(recipe.inputs)) {
            inv[mat] -= amount;
        }
        this._pendingRefund  = { ...recipe.inputs };
        this._craftingId     = recipeId;
        this._craftTimer     = recipe.craftTime;

        window.GameInterface?.pushConsoleLog?.(`CRAFTING ${recipe.name}...`);
        window.GameInterface?.updateCraftProgress?.(recipeId, 0);
        this._refreshUI();
        return true;
    }

    cancelCraft() {
        if (!this._craftingId) return;
        const inv = this._getInventory();
        if (inv && this._pendingRefund) {
            for (const [mat, amt] of Object.entries(this._pendingRefund)) {
                inv[mat] = (inv[mat] || 0) + amt;
            }
        }
        this._craftingId   = null;
        this._craftTimer   = 0;
        this._pendingRefund = null;
        window.GameInterface?.updateCraftProgress?.(null, 0);
        window.GameInterface?.pushConsoleLog?.('CRAFTING CANCELLED');
        this._refreshUI();
    }

    useShieldCell() {
        const vt  = window.VoxelTreadmill;
        const inv = this._getInventory();
        if (!vt || !inv) return false;

        if (!inv.shieldCells) {
            window.GameInterface?.pushConsoleLog?.('NO SHIELD CELLS');
            return false;
        }
        const maxShield = _effectiveMaxShield();
        if ((vt._shield || 0) >= maxShield) {
            window.GameInterface?.pushConsoleLog?.('SHIELD AT MAXIMUM');
            return false;
        }
        inv.shieldCells--;
        vt._shield = Math.min(maxShield, (vt._shield || 0) + SHIELD_RESTORE);
        window.GameInterface?.pushConsoleLog?.(
            `SHIELD CELL USED — SHIELD: ${Math.floor(vt._shield)}%`
        );
        window.GameInterface?.updateStats?.(vt._health, vt._shield);
        window.GameInterface?.updateShieldCells?.(inv.shieldCells);
        this._broadcast({ type: 'shield_used' });
        return true;
    }

    useMedkit() {
        const vt  = window.VoxelTreadmill;
        const inv = this._getInventory();
        if (!vt || !inv) return false;

        if (!inv.medkits) {
            window.GameInterface?.pushConsoleLog?.('NO MEDKITS');
            return false;
        }
        const maxHealth = _effectiveMaxHealth();
        if ((vt._health || 0) >= maxHealth) {
            window.GameInterface?.pushConsoleLog?.('HEALTH AT MAXIMUM');
            return false;
        }
        inv.medkits--;
        vt._health = Math.min(maxHealth, (vt._health || 0) + HEALTH_RESTORE);
        window.GameInterface?.pushConsoleLog?.(
            `MEDKIT USED — HEALTH: ${Math.floor(vt._health)}%`
        );
        window.GameInterface?.updateStats?.(vt._health, vt._shield);
        window.GameInterface?.updateMedkits?.(inv.medkits);
        return true;
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    _getRecipe(id) {
        return CRAFT_RECIPES.find(r => r.id === id) ?? null;
    }

    _getInventory() {
        const vt = window.VoxelTreadmill;
        if (!vt) return null;
        this._ensureInventorySlots();
        return vt._inventory;
    }

    _ensureInventorySlots() {
        const inv = window.VoxelTreadmill?._inventory;
        if (!inv) return;
        if (!('shieldCells' in inv)) inv.shieldCells = 0;
        if (!('medkits'     in inv)) inv.medkits     = 0;
    }

    _completeCraft() {
        const recipe = this._getRecipe(this._craftingId);
        const inv    = this._getInventory();
        if (recipe && inv) {
            inv[recipe.output.type] = (inv[recipe.output.type] || 0) + recipe.output.amount;
            const count = inv[recipe.output.type];
            window.GameInterface?.pushConsoleLog?.(
                `CRAFTED: ${recipe.icon} ${recipe.name} [${count}/${recipe.maxCarry}]`
            );
            window.GameInterface?.updateCraftProgress?.(this._craftingId, 1);
            window.GameInterface?.updateShieldCells?.(inv.shieldCells || 0);
            window.GameInterface?.updateMedkits?.(inv.medkits || 0);
            this._broadcast({ type: 'crafted', recipeId: this._craftingId });
        }
        this._craftingId   = null;
        this._craftTimer   = 0;
        this._pendingRefund = null;
        this._refreshUI();
    }

    _refreshUI() {
        const inv = this._getInventory();
        if (!inv) return;
        window.GameInterface?.updateResources?.(
            inv.alloy || 0, inv.biomass || 0, inv.titanium || 0,
            inv.plasma || 0, inv.crystal || 0
        );
        window.GameInterface?.updateShieldCells?.(inv.shieldCells || 0);
        window.GameInterface?.updateMedkits?.(inv.medkits || 0);
        window.GameInterface?.refreshCraftPanel?.();
    }

    _broadcast(msg) {
        try {
            window.OasisMultiplayer?._broadcast?.({
                ...msg,
                peerId: window.OasisMultiplayer._peer?.id,
            });
        } catch (_) {}
    }
}
